/*
    Lacrosse field geometry - pure data and math.

    This is the table `CourtGeometryBySport` would hold if a field were a court.
    It is not one: `CourtGeometryBySport.LACROSSE` is a single permissive
    SIMULATION VOLUME (the largest field either code allows, centred on the
    field), while this module is the MARKINGS, keyed by variant. Both are
    correct and they describe different rectangles; see the comment on
    CourtGeometryBySport.LACROSSE in @volley/physics.

    No THREE, no React, no colours, no layer heights, no scene frame. Metres
    and radians only, so every number can be asserted in a unit test without a
    canvas. The goal-relative translation the visualizer needs is applied by
    buildLacrosseLayout(), not here - keeping this module in the rulebook's own
    frame is what lets geometry.test.ts check the same numbers the field is
    drawn from.

    Frame: +x right, +z away, origin at the CENTRE of the field, centre line on
    z = 0, the two goal lines at z = +/-goalZ.

    Sources are the NCAA rulebooks read directly rather than from
    field-diagram sites, which disagree with each other on the restraining line
    because the two codes measure it from opposite ends:
      men's    NCAA Men's Lacrosse Rules, Rule 1 - "Men's Lacrosse Field of
               Play" diagram, and Sections 7 (restraining lines), 8 (wing
               areas), 9 (centre of the field).
      women's  NCAA Women's Lacrosse Rules, Rule 1 Sections 1 (dimensions),
               4 (restraining line), 5 (centre circle), 6-7 (arc and fan),
               8 (below-goal dots), 21 (goal circle), plus "Directions for
               8-Meter Arc / 12-Meter Fan".
*/

/** Feet, in metres. */
export const FT = 0.3048;
/** Yards, in metres. */
export const YD = 0.9144;

/*
    Which set of markings to draw.

    This lives here rather than in context/sport.tsx, which re-exports it, so
    the variant has exactly one home: geometry.ts owns both the list and the
    fields it keys, and a fourth variant is a one-file change. The other way
    round - sport.tsx defining the union and importing the spots derived from
    it - is a context -> Visualizer -> context import cycle.

    Presentation only. It must never reach the workout payload, coach, physics,
    the vision proto or the `sports` table.
*/
export const LacrosseVariants = ["MENS", "WOMENS", "MIXED"] as const;
export type LacrosseVariant = (typeof LacrosseVariants)[number];

export function isValidLacrosseVariant(
    maybeVariant: string,
): maybeVariant is LacrosseVariant {
    return LacrosseVariants.includes(maybeVariant as LacrosseVariant);
}

/**
 * One variant's field of play and its markings, in metres.
 *
 * An ABSENT key means "this variant has no such marking". Every consumer below
 * tests for presence rather than for variant name, so adding a variant needs
 * no change to any renderer.
 */
export interface LacrosseFieldGeometry {
    label: string;
    /** Half the length of the field of play. */
    halfL: number;
    /** Half the width of the field of play. */
    halfW: number;
    /** Goal line, from the field centre. */
    goalZ: number;
    /**
     * Restraining line, from the field CENTRE - not from the goal line.
     *
     * Both codes land theirs 20 yd from centre, which is the coincidence that
     * makes secondary sources contradict each other: men's measure 20 yd from
     * the goal line (§7) and women's 30 yd (§4), off goal lines that are 15 yd
     * and 10 yd from their own end lines.
     */
    restrainZ: number;
    /** Men's crease: a full circle about the goal. */
    creaseR?: number;
    /** Men's wing lines, offset from the centre of the field. */
    wingX?: number;
    /** Men's wing lines, running this far each side of the centre line. */
    wingZ?: number;
    /** Men's centre marking: a contrasting square AT the centre. */
    centreSquare?: number;
    /** Women's goal circle, to the OUTER edge of the painted line. */
    goalCircleR?: number;
    /** Women's 8 m arc, from the goal-line centre. */
    arcR?: number;
    /** Women's 12 m fan, from the goal-line centre. A semicircle. */
    fanR?: number;
    /** Women's 45-degree legs, closing the 8 m arc onto the goal circle. */
    angleLeg?: number;
    /** Women's centre circle. */
    centreCircleR?: number;
    /** Women's draw line, through the centre, parallel to the goal lines. */
    drawLine?: number;
    /** Women's below-goal dots, this far behind the goal line. */
    dotBack?: number;
    /** Mixed only: the women's restraining line, off the men's goal line. */
    womensRestrainFromGoal?: number;
}

/*
    Men's and women's lacrosse are not one field with a switch: different
    footprint, different goal position, and a women's field carries the 8 m arc
    and 12 m fan that a men's field has no equivalent of. Fields are selected
    by variant, never derived from one another.
*/
export const LACROSSE_FIELDS: Record<LacrosseVariant, LacrosseFieldGeometry> = {
    MENS: {
        label: "Men's",
        halfL: 55 * YD, // 110 x 60 yd field of play
        halfW: 30 * YD,
        goalZ: 40 * YD, // goal line 15 yd off the end line
        restrainZ: 20 * YD, // 20 yd from the goal line
        creaseR: 3 * YD, // 9 ft radius
        wingX: 20 * YD, // wing lines 20 yd off the centre of the field
        wingZ: 10 * YD, // running 10 yd each side of the centre line
        centreSquare: (4 / 12) * FT, // 4 in contrasting square AT the centre
    },

    WOMENS: {
        label: "Women's",
        halfL: 60 * YD, // 120 x 65 yd optimal (110-120 x 60-70 allowed)
        halfW: 32.5 * YD,
        goalZ: 50 * YD, // goals 100 yd apart => 10 yd off the end line
        restrainZ: 20 * YD, // 30 yd from the goal line, landing 20 yd from centre
        goalCircleR: 2.6, // 8'6", to the OUTER edge of the painted line
        arcR: 10.6, // 8 m arc: 34'10" from the goal-line centre
        fanR: 14.6, // 12 m fan: 47'9", a semicircle
        angleLeg: 8.6106, // 28'3" legs at 45 deg off the goal line
        centreCircleR: 15 * FT, // 30 ft diameter
        drawLine: 3.023, // 9'11", parallel to the goal lines
        dotBack: 5 * YD, // below-goal dots, 5 yd behind the goal line
    },

    /*
        A real dual-marked field, not a display mode.

        Men's footprint (110 x 60 is legal for women's too, which allows
        110-120 x 60-70) and ONE goal per end carrying both sets of markings -
        a trainer aims at a goal, so two goals 5 yd apart would be worse than
        useless.

        The women's restraining line is therefore measured off the men's goal
        line, landing 30 yd out and so 10 yd from centre, and BOTH restraining
        lines show. That is correct, and it is what these fields look like in
        person.
    */
    MIXED: {
        label: "Mixed",
        halfL: 55 * YD,
        halfW: 30 * YD,
        goalZ: 40 * YD,
        restrainZ: 20 * YD,
        creaseR: 3 * YD,
        wingX: 20 * YD,
        wingZ: 10 * YD,
        centreSquare: (4 / 12) * FT,
        goalCircleR: 2.6,
        arcR: 10.6,
        fanR: 14.6,
        angleLeg: 8.6106,
        centreCircleR: 15 * FT,
        drawLine: 3.023,
        dotBack: 5 * YD,
        womensRestrainFromGoal: 30 * YD,
    },
};

/** A 6 x 6 ft cage. Identical in both codes. */
export const GOAL = {
    halfWidth: 0.915, // 6 ft mouth
    height: 1.83, // 6 ft crossbar
    pipeRadius: 0.045,
    netDepth: 2.2, // netting tapers to a floor point this far behind
};

/**
 * Painted line width.
 *
 * Field paint and court paint are the same 2 in, so this must equal
 * ProceduralCourt's LINE_WIDTH - asserted in geometry.test.ts. It is duplicated
 * rather than imported because that module pulls in THREE and React, which
 * would cost this one its headless testability.
 *
 * There is deliberately no LINE_Y here: how high a marking floats above the
 * turf is a rendering decision, and COURT_LAYER_Y already owns the whole
 * overlay stack.
 */
export const LINE_W = 0.05;

/** Grass runs right up to the field of play; this is how much of it to draw. */
export const APRON = { side: 3.5, back: 4 };

/**
 * The marking radii the trainer positions and the drill rings are built from.
 *
 * `goalCircleR`, `arcR`, `fanR` and `angleLeg` are IDENTICAL in WOMENS and
 * MIXED and absent from MENS, so nothing a trainer spot or a drill ring uses
 * varies by variant - which is why one frozen set of positions serves all
 * three fields. A men's field gets the arc and the fan as dashed drill rings
 * rather than paint.
 *
 * Read off WOMENS rather than retyped, so there is one copy of each number.
 * geometry.test.ts asserts MIXED agrees.
 */
export const DRILL_GEOMETRY = {
    goalCircleR: LACROSSE_FIELDS.WOMENS.goalCircleR!,
    arcR: LACROSSE_FIELDS.WOMENS.arcR!,
    fanR: LACROSSE_FIELDS.WOMENS.fanR!,
    angleLeg: LACROSSE_FIELDS.WOMENS.angleLeg!,
    dotBack: LACROSSE_FIELDS.WOMENS.dotBack!,
} as const;

/** One painted straight segment, in the field frame. */
export interface FieldLine {
    x1: number;
    z1: number;
    x2: number;
    z2: number;
}

/**
 * One painted arc, in the field frame.
 *
 * Bearings are measured so a bearing `b` points along the world direction
 * `(x, z) = (cos b, sin b)`. Bearing 0 is +x; bearing PI/2 is +z. `sweep` is
 * signed, in radians.
 */
export interface FieldArc {
    cx: number;
    cz: number;
    r: number;
    from: number;
    sweep: number;
}

/**
 * Half-extent of the 8 m arc, off the downfield axis, in radians.
 *
 * Derived from the rulebook's own construction rather than chosen: the 45
 * degree legs run `angleLeg / sqrt(2)` out and along from the goal circle, and
 * where they land IS the end of the arc. So this one number fixes the arc's
 * angular extent, and geometry.test.ts checks the legs land on the arc.
 *
 * Null for a variant with no arc.
 */
export function arcHalfAngle(V: LacrosseFieldGeometry): number | null {
    if (V.arcR == null || V.goalCircleR == null || V.angleLeg == null) {
        return null;
    }
    const leg = V.angleLeg / Math.SQRT2;
    return Math.atan2(V.goalCircleR + leg, leg);
}

/*
    The straight markings, one small function per group.

    Split out of a single fieldLines() that had grown to fifteen branches. Each
    of these owns one rulebook section, so a marking can be read, checked and
    corrected without holding the rest in your head - and the "absent key means
    no such marking" convention now reads as one guard per function instead of
    one clause in a long chain.
*/

/** The field of play itself: end lines, sidelines, centre line. */
function perimeterLines(V: LacrosseFieldGeometry): FieldLine[] {
    const hl = V.halfL;
    const hw = V.halfW;
    return [
        { x1: -hw, z1: -hl, x2: hw, z2: -hl }, // end lines
        { x1: -hw, z1: hl, x2: hw, z2: hl },
        { x1: -hw, z1: -hl, x2: -hw, z2: hl }, // sidelines
        { x1: hw, z1: -hl, x2: hw, z2: hl },
        { x1: -hw, z1: 0, x2: hw, z2: 0 }, // centre line
    ];
}

/** The restraining line and the goal line, at one end. */
function endLines(V: LacrosseFieldGeometry, m: number): FieldLine[] {
    const gw = GOAL.halfWidth;
    return [
        { x1: -V.halfW, z1: m * V.restrainZ, x2: V.halfW, z2: m * V.restrainZ },
        { x1: -gw, z1: m * V.goalZ, x2: gw, z2: m * V.goalZ },
    ];
}

/** Men's wing lines: parallel to the sidelines, either side of centre. */
function wingLines(V: LacrosseFieldGeometry, m: number): FieldLine[] {
    if (V.wingX == null || V.wingZ == null) return [];
    return [{ x1: m * V.wingX, z1: -V.wingZ, x2: m * V.wingX, z2: V.wingZ }];
}

/**
 * Women's: the flat sides of the 12 m fan, which run along the goal line
 * extended from the goal circle out to where the semicircle lands, and the
 * 45-degree legs that close the 8 m arc onto the same two points.
 */
function fanSideLines(V: LacrosseFieldGeometry, m: number): FieldLine[] {
    if (V.fanR == null || V.goalCircleR == null || V.angleLeg == null) {
        return [];
    }
    const gz = m * V.goalZ;
    const fd = -m; // downfield direction in z
    const b = V.goalCircleR;
    const leg = V.angleLeg / Math.SQRT2;
    const out: FieldLine[] = [];
    for (const sx of [1, -1]) {
        out.push({ x1: sx * b, z1: gz, x2: sx * V.fanR, z2: gz });
        out.push({
            x1: sx * b,
            z1: gz,
            x2: sx * (b + leg),
            z2: gz + fd * leg,
        });
    }
    return out;
}

/**
 * Women's below-goal dots.
 *
 * Drawn as short cross-field ticks rather than the rulebook's small circles -
 * at any camera distance the app uses, a 1 ft circle is a dot either way.
 */
function belowGoalDots(V: LacrosseFieldGeometry, m: number): FieldLine[] {
    if (V.dotBack == null || V.goalCircleR == null) return [];
    const dz = m * (V.goalZ + V.dotBack);
    if (Math.abs(dz) >= V.halfL) return [];
    return [1, -1].map((sx) => ({
        x1: sx * V.goalCircleR! - 0.15,
        z1: dz,
        x2: sx * V.goalCircleR! + 0.15,
        z2: dz,
    }));
}

/** Mixed only: the women's restraining line, measured off the men's goal line. */
function womensRestrainingLine(
    V: LacrosseFieldGeometry,
    m: number,
): FieldLine[] {
    if (V.womensRestrainFromGoal == null) return [];
    const wz = m * (V.goalZ - V.womensRestrainFromGoal);
    return [{ x1: -V.halfW, z1: wz, x2: V.halfW, z2: wz }];
}

/**
 * Centre marking.
 *
 * Men's is a 4 in contrasting SQUARE (Rule 1-9) - not an X, which is what
 * field-diagram sites tend to show. Women's is a 9'11" draw line through the
 * centre point, parallel to the goal lines. A mixed field carries both.
 */
function centreLines(V: LacrosseFieldGeometry): FieldLine[] {
    const out: FieldLine[] = [];
    if (V.centreSquare != null) {
        const h = V.centreSquare / 2;
        out.push({ x1: -h, z1: -h, x2: h, z2: -h });
        out.push({ x1: -h, z1: h, x2: h, z2: h });
        out.push({ x1: -h, z1: -h, x2: -h, z2: h });
        out.push({ x1: h, z1: -h, x2: h, z2: h });
    }
    if (V.drawLine != null) {
        out.push({ x1: -V.drawLine / 2, z1: 0, x2: V.drawLine / 2, z2: 0 });
    }
    return out;
}

/** Every straight painted segment, in metres, for one variant. */
export function fieldLines(V: LacrosseFieldGeometry): FieldLine[] {
    const perEnd = [1, -1].flatMap((m) => [
        ...endLines(V, m),
        ...wingLines(V, m),
        ...fanSideLines(V, m),
        ...belowGoalDots(V, m),
        ...womensRestrainingLine(V, m),
    ]);
    return [...perimeterLines(V), ...perEnd, ...centreLines(V)];
}

/** Curved painted markings, in metres, for one variant. */
export function fieldArcs(V: LacrosseFieldGeometry): FieldArc[] {
    const A: FieldArc[] = [];
    const full = (cx: number, cz: number, r: number) =>
        A.push({ cx, cz, r, from: 0, sweep: Math.PI * 2 });
    const half = arcHalfAngle(V);

    for (const m of [1, -1]) {
        const gz = m * V.goalZ;
        const fd = -m; // downfield in z, from this goal
        const toField = fd > 0 ? Math.PI / 2 : -Math.PI / 2;

        // Men's crease - a full 9 ft circle around the goal.
        if (V.creaseR != null) full(0, gz, V.creaseR);

        /*
            Women's goal circle - 8'6" to the OUTER edge, so the painted band
            sits just inside the nominal radius rather than straddling it.
        */
        if (V.goalCircleR != null) full(0, gz, V.goalCircleR - LINE_W / 2);

        // 8 m arc, closed at each end by the 45-degree legs. See arcHalfAngle.
        if (V.arcR != null && half != null) {
            A.push({
                cx: 0,
                cz: gz,
                r: V.arcR,
                from: toField - half,
                sweep: half * 2,
            });
        }

        // 12 m fan - a semicircle; its flat sides are straight segments.
        if (V.fanR != null) {
            A.push({
                cx: 0,
                cz: gz,
                r: V.fanR,
                from: toField - Math.PI / 2,
                sweep: Math.PI,
            });
        }
    }

    if (V.centreCircleR != null) full(0, 0, V.centreCircleR);
    return A;
}

/**
 * Short straight ticks that belong with the arcs - currently the centre hash
 * on the 8 m arc, sitting on the goal-line perpendicular.
 */
export function arcHashes(V: LacrosseFieldGeometry): FieldLine[] {
    if (V.arcR == null) return [];
    const out: FieldLine[] = [];
    for (const m of [1, -1]) {
        const hz = m * V.goalZ + -m * V.arcR;
        out.push({ x1: -0.152, z1: hz, x2: 0.152, z2: hz }); // 1 ft
    }
    return out;
}

/** One alternating mow stripe, in the field frame. */
export interface MowStripe {
    x: number;
    z: number;
    w: number;
    d: number;
}

/**
 * Alternating mow stripes, 10 yd deep. Cosmetic, and the reason a bare field
 * still reads as a field from the overhead camera.
 */
export function mowStripes(V: LacrosseFieldGeometry): MowStripe[] {
    const out: MowStripe[] = [];
    const band = 10 * YD;
    const n = Math.round((V.halfL * 2) / band);
    for (let i = 0; i < n; i++) {
        if (i % 2 === 0) continue;
        out.push({
            x: 0,
            z: -V.halfL + band * (i + 0.5),
            w: V.halfW * 2,
            d: band,
        });
    }
    return out;
}

/** Where a cage stands, and which way its netting tapers. */
export interface GoalPlacement {
    x: number;
    z: number;
    /**
     * Which end of the field this cage belongs to: +1 for the +z goal line,
     * -1 for the -z one. The netting tapers AWAY from the field, so this is
     * not cosmetic.
     */
    sideSign: 1 | -1;
}

/**
 * Clear grass between the two cages' inner posts, in the two-cage drill.
 *
 * 8 ft is the spec, and it is a working dimension rather than a round number:
 * a machine is about 4 ft across, so an 8 ft gap is what lets one stand
 * BETWEEN the cages with room to walk it in and out.
 */
export const TWO_CAGE_GAP = 8 * FT;

/**
 * Two-cage spacing, centre to centre.
 *
 * Derived from the gap rather than stated, because the gap is what a coach
 * measures out on the grass - two tape lengths from an inner post. Was a bare
 * 12 ft (a 6 ft gap), which the machine does not fit through.
 */
export const TWO_GOAL_OFFSET_X = 2 * GOAL.halfWidth + TWO_CAGE_GAP;

/**
 * Where the cages go. Both codes put one on each goal line.
 *
 * `twoGoals` replaces the drill-end cage with a PAIR straddling the goal's own
 * centreline, half the spacing either side of it. The PAINT does not move -
 * the crease, the goal circle, the 8 m arc and the 12 m fan stay centred where
 * they are - which is what a real two-cage drill looks like: two portable
 * cages set either side of the painted goal's centre.
 *
 * Straddling rather than "keep one cage at 0 and add a second beside it",
 * which is what this used to do. The machine stands on the line bisecting the
 * two cages, so that line is the one thing a coach has to find, and straddling
 * makes it the line the arc's centre hash, the fan's apex and every painted
 * centre marking already agree on. Appended to one side instead, the machine's
 * line would sit 7 ft off every mark on the field.
 *
 * Changed from the reference renderer, which derived `sideSign` as
 * `Math.sign(p.z) || -1`. That works only while the goal lines are at
 * +/-goalZ: the visualizer translates the drill-end goal onto scene z = 0,
 * where `Math.sign` returns 0 and every cage silently falls back to -1. The
 * side is stated here instead, and the second cage inherits the first's.
 */
export function goalPositions(
    V: LacrosseFieldGeometry,
    {
        drillEndSign = -1,
        twoGoals = false,
        extraGoalOffsetX = TWO_GOAL_OFFSET_X,
    }: {
        drillEndSign?: 1 | -1;
        twoGoals?: boolean;
        extraGoalOffsetX?: number;
    } = {},
): GoalPlacement[] {
    const out: GoalPlacement[] = [
        { x: 0, z: V.goalZ, sideSign: 1 },
        { x: 0, z: -V.goalZ, sideSign: -1 },
    ];
    if (twoGoals) {
        // Slide the drill-end cage half a spacing off centre and put its pair
        // on the other side, so x = 0 - the painted goal's own centreline -
        // bisects them.
        const half = extraGoalOffsetX / 2;
        const drillIndex = drillEndSign === 1 ? 0 : 1;
        out[drillIndex] = { ...out[drillIndex], x: -half };
        out.push({
            x: half,
            z: drillEndSign * V.goalZ,
            sideSign: drillEndSign,
        });
    }
    return out;
}
